import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { DbModuleConfigResponse } from '../../services/module4Service';

interface ConfigurationDetailsPanelProps {
  configData: DbModuleConfigResponse;
}

const ConfigurationDetailsPanel: React.FC<ConfigurationDetailsPanelProps> = ({ configData }) => {
  const config = configData.config_data;
  
  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" component="h2" gutterBottom>
        Configuration Details
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Name:</Typography>
            <Typography variant="body1">{configData.name || config.name}</Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Config ID:</Typography>
            <Typography variant="body1">{config.config_id}</Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Description:</Typography>
            <Typography variant="body1">{configData.description || config.description}</Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="subtitle1">Topic Mapping</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1}>
            {Object.entries(config.topic_mapping || {}).map(([key, value]) => (
              <Grid item xs={12} key={key}>
                <Typography variant="body2">
                  <Box component="span" fontWeight="bold">{key}:</Box> {typeof value === 'string' ? value : JSON.stringify(value)}
                </Typography>
              </Grid>
            ))}
            {Object.keys(config.topic_mapping || {}).length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">No topic mappings defined</Typography>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="subtitle1">Prompt Configuration</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">System Prompt:</Typography>
              <Box 
                sx={{ 
                  p: 2, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1, 
                  my: 1,
                  overflow: 'auto',
                  maxHeight: '200px',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.875rem'
                }}
              >
                {config.prompt?.system_prompt || 'No system prompt defined'}
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Output Format:</Typography>
              <Typography variant="body2">{config.prompt?.output_format || 'Not specified'}</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Temperature:</Typography>
              <Typography variant="body2">{config.prompt?.temperature}</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Max Tokens:</Typography>
              <Typography variant="body2">{config.prompt?.max_tokens}</Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="subtitle1">Results Configuration</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Output Format:</Typography>
              <Typography variant="body2">{config.results?.output_format || 'Not specified'}</Typography>
            </Grid>
            
            {config.results?.output_regex && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Output Regex:</Typography>
                <Typography variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace',
                    bgcolor: 'grey.100',
                    p: 1,
                    borderRadius: 1
                  }}
                >
                  {config.results.output_regex}
                </Typography>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Result Columns:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {config.results?.result_columns?.map((column, index) => {
                  // Handle both string and object formats
                  const columnLabel = typeof column === 'string' 
                    ? column 
                    : (column as any)?.name || JSON.stringify(column);
                  return <Chip key={index} label={columnLabel} size="small" />;
                })}
                {(!config.results?.result_columns || config.results.result_columns.length === 0) && (
                  <Typography variant="body2" color="text.secondary">No result columns defined</Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default ConfigurationDetailsPanel;
